import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Stack, Chip, Button, MenuItem, Select,
  FormControl, InputLabel, CircularProgress, IconButton,
  TextField, InputAdornment, Paper, Grid,
} from '@mui/material';
import {
  Hotel, Search as SearchIcon, Close as CloseIcon,
  LocalHospital,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  loadWards_bed, loadBedsByWarddb, erDirectAdmitdb,
} from '../../BaseURL';

const PRIMARY      = '#1A9B7F';
const PRIMARY_DARK = '#157763';
const PRIMARY_BG   = 'rgba(26,155,127,0.10)';

/**
 * Post-registration ER bed assignment dialog.
 *
 * Mirrors the Step 1 (ward) → Step 2 (bed) layout used in Wards/Home.js so the
 * pick experience is identical to regular bed management. Calls /erDirectAdmit
 * which inserts the admission row, flips the patient to inPatient + Emergency,
 * and occupies the bed.
 */
export default function BedAssignmentDialog({
  open, onClose, patient, currentUser, onAssigned,
}) {
  const [wards, setWards]               = useState([]);
  const [beds, setBeds]                 = useState([]);
  const [selectedWard, setSelectedWard] = useState('');
  const [selectedBed, setSelectedBed]   = useState(null);
  const [search, setSearch]             = useState('');
  const [loadingWards, setLoadingWards] = useState(false);
  const [loadingBeds, setLoadingBeds]   = useState(false);
  const [submitting, setSubmitting]     = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedWard('');
    setSelectedBed(null);
    setBeds([]);
    setSearch('');

    setLoadingWards(true);
    axios.get(loadWards_bed)
      .then(res => setWards(res.data?.wards || []))
      .catch(err => console.error('loadWards_bed failed', err))
      .finally(() => setLoadingWards(false));
  }, [open]);

  useEffect(() => {
    if (!selectedWard) { setBeds([]); return; }
    setSelectedBed(null);
    setLoadingBeds(true);
    axios.get(`${loadBedsByWarddb}?wardId=${selectedWard}`)
      .then(res => setBeds(res.data?.beds || []))
      .catch(err => console.error('loadBedsByWard failed', err))
      .finally(() => setLoadingBeds(false));
  }, [selectedWard]);

  const filteredBeds = useMemo(() => {
    if (!search.trim()) return beds;
    const q = search.toLowerCase();
    return beds.filter(b => String(b.bed_number || '').toLowerCase().includes(q));
  }, [beds, search]);

  const handleConfirm = async () => {
    if (!patient?.id || !selectedBed?.id || !selectedWard) {
      Swal.fire('Missing info', 'Pick a ward and a bed before confirming.', 'warning');
      return;
    }
    const wardName  = wards.find(w => w.id === selectedWard)?.name || '—';
    const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));
    const initials  = (patient.name || '?').split(/\s+/).filter(Boolean).slice(0,2).map(s=>s[0]).join('').toUpperCase();

    const ok = await Swal.fire({
      title: 'Confirm ER Bed Assignment',
      html: `
        <div style="text-align:left;font-family:Inter,Segoe UI,sans-serif;">
          <div style="display:flex;align-items:center;gap:12px;padding:12px;border:1px solid #e5e9ef;border-radius:10px;background:#f8fafc;">
            <div style="width:48px;height:48px;border-radius:50%;background:#dc2626;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:1.05rem;">${initials}</div>
            <div style="flex:1;min-width:0;">
              <div style="font-weight:700;color:#0f172a;font-size:1rem;line-height:1.2;">${escapeHtml(patient.name)}</div>
              <div style="color:#64748b;font-size:0.8rem;margin-top:2px;">ID: <strong>${escapeHtml(patient.id)}</strong>${patient.age ? ` · ${escapeHtml(patient.age)} yrs` : ''}${patient.gender ? ` · ${escapeHtml(patient.gender)}` : ''}</div>
            </div>
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:10px;">
            <div style="padding:10px;border:1px solid #e5e9ef;border-radius:8px;background:#fff;">
              <div style="font-size:0.7rem;color:#94a3b8;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;">Ward</div>
              <div style="font-weight:700;color:#0f172a;margin-top:2px;">${escapeHtml(wardName)}</div>
            </div>
            <div style="padding:10px;border:1px solid #e5e9ef;border-radius:8px;background:#fff;">
              <div style="font-size:0.7rem;color:#94a3b8;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;">Bed</div>
              <div style="font-weight:700;color:#0f172a;margin-top:2px;">${escapeHtml(selectedBed.bed_number)}${selectedBed.daily_rate ? ` <span style="color:#64748b;font-weight:500;font-size:0.8rem;">· GHS ${selectedBed.daily_rate}/day</span>` : ''}</div>
            </div>
          </div>

          <div style="margin-top:10px;padding:8px 12px;border-radius:8px;background:#FEE2E2;color:#991B1B;font-weight:700;font-size:0.85rem;">
            🚨 Final status will be <strong>EMERGENCY</strong> — patient appears on ER InPatient with the live timer.
          </div>

          <p style="margin-top:14px;color:#475569;font-size:0.82rem;">
            Once confirmed: bed marked <strong>occupied</strong>, patient becomes an <strong>inpatient</strong>, ER timer starts now.
          </p>
        </div>
      `,
      icon: undefined,
      showCancelButton: true,
      confirmButtonText: 'Yes, Assign Bed',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      width: 560,
      focusCancel: true,
    });
    if (!ok.isConfirmed) return;

    setSubmitting(true);
    try {
      const res = await axios.post(erDirectAdmitdb, {
        patientId:   patient.id,
        patientName: patient.name,
        age:         patient.age,
        gender:      patient.gender,
        bedId:       selectedBed.id,
        wardId:      selectedWard,
        currentUser,
      });
      if (res.data?.success) {
        onClose && onClose();
        onAssigned && onAssigned(patient);
      } else {
        throw new Error(res.data?.message || 'Assignment failed');
      }
    } catch (err) {
      console.error('erDirectAdmit failed', err);
      Swal.fire('Error', err.response?.data?.message || err.message || 'Failed to assign bed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: PRIMARY, color: 'white', py: 2,
      }}>
        <LocalHospital />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>
            ER Bed Assignment
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
            {patient?.name ? `${patient.name} · ID ${patient.id}` : 'Patient registration → bed pick'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
        {/* Step 1 — Ward */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: '#0f172a' }}>
            🏥 Step 1: Select Ward
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select Ward *</InputLabel>
            <Select
              value={selectedWard}
              label="Select Ward *"
              onChange={(e) => setSelectedWard(e.target.value)}
              disabled={loadingWards}
              sx={{ bgcolor: 'white' }}
            >
              {loadingWards && (
                <MenuItem disabled value="">
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
                </MenuItem>
              )}
              {!loadingWards && wards.length === 0 && (
                <MenuItem disabled value="">No wards configured</MenuItem>
              )}
              {wards.map(w => (
                <MenuItem key={w.id} value={w.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocalHospital fontSize="small" sx={{ color: PRIMARY }} />
                    <Typography>{w.name}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Step 2 — Bed */}
        {selectedWard && (
          <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }}
                   justifyContent="space-between" spacing={1.5} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0f172a' }}>
                🛏️ Step 2: Select Available Bed
              </Typography>
              <TextField
                size="small"
                placeholder="Search bed #"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 200, bgcolor: 'white' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {loadingBeds && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={28} sx={{ color: PRIMARY }} />
              </Box>
            )}

            {!loadingBeds && filteredBeds.length === 0 && (
              <Paper sx={{
                p: 4, textAlign: 'center',
                bgcolor: 'grey.50', border: '2px dashed', borderColor: 'grey.300',
              }}>
                <Typography variant="body1" color="text.secondary">
                  No available beds in this ward
                </Typography>
              </Paper>
            )}

            {!loadingBeds && filteredBeds.length > 0 && (
              <Grid container spacing={2}>
                {filteredBeds.map(bed => {
                  const active = selectedBed?.id === bed.id;
                  return (
                    <Grid item xs={6} sm={4} md={3} key={bed.id}>
                      <Paper
                        onClick={() => setSelectedBed(bed)}
                        elevation={active ? 6 : 1}
                        sx={{
                          p: 2.5,
                          textAlign: 'center',
                          cursor: 'pointer',
                          bgcolor: active ? PRIMARY : 'white',
                          color: active ? 'white' : 'text.primary',
                          border: '2px solid',
                          borderColor: active ? PRIMARY_DARK : 'grey.200',
                          borderRadius: 2,
                          transition: 'all .25s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4,
                            borderColor: PRIMARY,
                            bgcolor: active ? PRIMARY_DARK : PRIMARY_BG,
                          },
                        }}
                      >
                        <Hotel sx={{ fontSize: 36, mb: 0.5 }} />
                        <Typography variant="h6" fontWeight={700}>
                          {bed.bed_number}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                          {active ? '✓ Selected' : 'Available'}
                        </Typography>
                        {bed.daily_rate ? (
                          <Typography variant="caption" sx={{
                            display: 'block', mt: 0.5,
                            opacity: active ? 0.9 : 0.6,
                            fontSize: '0.7rem',
                          }}>
                            GHS {bed.daily_rate}/day
                          </Typography>
                        ) : null}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: '#fff' }}>
        <Button onClick={onClose} disabled={submitting} sx={{ color: '#64748b' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedBed || submitting}
          sx={{
            bgcolor: PRIMARY,
            '&:hover': { bgcolor: PRIMARY_DARK },
            textTransform: 'none', fontWeight: 700, px: 3,
          }}
        >
          {submitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Assign Bed'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
